import React, { useState } from 'react';
import { useCanvas } from '../../../../context/CanvasContext';
import { tutorialAttr } from '../../../../tutorials';
import KeyBadge from './KeyBadge';
import KeyPicker from './KeyPicker';
import useKeyHoverHighlight from './useKeyHoverHighlight';
import { KEY_LIST_FIELDS, keyName, patchForAddingKey, patchForRemovingKey } from './keyGraph';
import keysUi from './styles/Keys.module.css';
import keyStyles from '../styles/EditSidebarKeys.module.css';

const COPY = {
    [KEY_LIST_FIELDS.GRANTS]: {
        role: 'grant',
        heading: 'Keys picked up here',
        hint: {
            situation: 'Players pick up these keys when they arrive at this situation.',
            choice: 'Players pick up these keys when they pick this choice.',
        },
        empty: 'No keys yet.',
        add: '+ Add key',
        picker: 'Pick a key to add',
    },
    [KEY_LIST_FIELDS.REMOVES]: {
        role: 'remove',
        heading: 'Keys taken away here',
        hint: {
            situation: 'Players lose these keys when they arrive at this situation.',
            choice: 'Players lose these keys when they pick this choice.',
        },
        empty: 'No keys taken away.',
        add: '+ Take away a key',
        picker: 'Pick a key to take away',
    },
};

/**
 * One of a situation's or choice's two key lists — the keys picked up there, or
 * the keys taken away there — as chips with a remove button and a picker.
 *
 * Both lists go through here so they read and behave identically. A key can be
 * in only one of them: adding it to one moves it out of the other in the same
 * op (see keyGraph.patchForAddingKey).
 *
 * Give it a `key` tied to the subject's id so an open picker closes when the
 * selection moves.
 *
 * @param {{
 *   field: 'grantsKeys'|'removesKeys',
 *   subject: 'situation'|'choice',
 *   data: object,                     the node's or edge's data
 *   commit: (patch: object) => void,
 *   tutorialTarget?: string,
 * }} props
 */
const KeyListEditor = ({ field, subject, data, commit, tutorialTarget }) => {
    const { keys } = useCanvas();
    const hoverKey = useKeyHoverHighlight();
    const [isPicking, setIsPicking] = useState(false);

    const copy = COPY[field];
    if (!copy) return null;
    const keyIds = Array.isArray(data?.[field]) ? data[field] : [];

    const apply = (patch) => {
        if (patch) commit(patch);
    };

    return (
        <div className={keyStyles.keyListSection}>
            <h3>{copy.heading}</h3>
            <p className={keysUi.sectionHint}>{copy.hint[subject]}</p>
            {keyIds.length > 0 ? (
                <ul className={keysUi.badgeList}>
                    {keyIds.map((keyId) => (
                        <li key={keyId}>
                            <KeyBadge
                                role={copy.role}
                                name={keyName(keys, keyId)}
                                onHoverChange={hoverKey(keyId)}
                                onRemove={() => apply(patchForRemovingKey(data, field, keyId))}
                            />
                        </li>
                    ))}
                </ul>
            ) : (
                <p className={keysUi.muted}>{copy.empty}</p>
            )}

            {isPicking ? (
                <KeyPicker
                    heading={copy.picker}
                    excludeIds={keyIds}
                    onPick={(keyId) => {
                        apply(patchForAddingKey(data, field, keyId));
                        setIsPicking(false);
                    }}
                    onCancel={() => setIsPicking(false)}
                />
            ) : (
                <button
                    type="button"
                    onClick={() => setIsPicking(true)}
                    className={keyStyles.grantKeyButton}
                    {...(tutorialTarget ? tutorialAttr(tutorialTarget) : {})}
                >
                    {copy.add}
                </button>
            )}
        </div>
    );
};

export default KeyListEditor;
